import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast.ts";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Role {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  permission_count?: number;
  user_count?: number;
}

export interface Permission {
  id: string;
  resource: string;
  action: string;
  description: string | null;
  is_system: boolean;
}

export interface RoleWithPermissions extends Role {
  permissions: Permission[];
}

export interface UserRoleAssignment {
  id: string;
  user_id: string;
  role_id: string;
  scope: { groups?: string[] };
  granted_by: string | null;
  granted_at: string;
  role: Role;
}

// ─── Roles ───────────────────────────────────────────────────────────────────

export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await fetchApi<{ roles: Role[] }>({
        url: "/v2/roles",
        method: "GET",
      });
      return data.roles;
    },
  });
};

export const useRole = (roleId: string) => {
  return useQuery({
    queryKey: ["roles", roleId],
    queryFn: async () => {
      const { data } = await fetchApi<{ role: RoleWithPermissions }>({
        url: `/v2/roles/${roleId}`,
        method: "GET",
      });
      return data.role;
    },
    enabled: !!roleId,
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      permissionIds?: string[];
    }) => {
      const { data } = await fetchApi<{ role: Role }>({
        url: "/v2/roles",
        method: "POST",
        data: payload,
      });
      return data.role;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast({ title: "Role created successfully" });
    },
    onError: () => {
      toast({ title: "Failed to create role", variant: "destructive" });
    },
  });
};

export const useUpdateRole = (roleId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      name?: string;
      description?: string | null;
      permissionIds?: string[];
    }) => {
      const { data } = await fetchApi<{ role: Role }>({
        url: `/v2/roles/${roleId}`,
        method: "PATCH",
        data: payload,
      });
      return data.role;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["roles", roleId] });
      // If the current viewer is assigned to this role, their abilities on
      // the client are now stale. Refetching /me brings back a fresh rule
      // set so the UI updates without a page reload.
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      toast({ title: "Role updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update role", variant: "destructive" });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (roleId: string) => {
      await fetchApi({ url: `/v2/roles/${roleId}`, method: "DELETE" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast({ title: "Role deleted successfully" });
    },
    onError: () => {
      toast({ title: "Failed to delete role", variant: "destructive" });
    },
  });
};

// ─── Permissions ─────────────────────────────────────────────────────────────

export const usePermissions = () => {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const { data } = await fetchApi<{ permissions: Permission[] }>({
        url: "/v2/roles/permissions",
        method: "GET",
      });
      return data.permissions;
    },
  });
};

// ─── User Role Assignments ───────────────────────────────────────────────────

export const useUserRoles = (userId: string) => {
  return useQuery({
    queryKey: ["user-roles", userId],
    queryFn: async () => {
      const { data } = await fetchApi<{
        userRoles: UserRoleAssignment[];
      }>({
        url: `/v2/roles/users/${userId}`,
        method: "GET",
      });
      return data.userRoles;
    },
    enabled: !!userId,
  });
};

export const useAssignRole = (userId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      roleId: string;
      scope?: { groups?: string[] };
    }) => {
      const { data } = await fetchApi<{
        userRole: UserRoleAssignment;
      }>({
        url: `/v2/roles/users/${userId}`,
        method: "POST",
        data: payload,
      });
      return data.userRole;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-roles", userId] });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast({ title: "Role assigned successfully" });
    },
    onError: () => {
      toast({ title: "Failed to assign role", variant: "destructive" });
    },
  });
};

export const useUpdateUserRoleScope = (userId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      roleId: string;
      scope: { groups?: string[] };
    }) => {
      const { data } = await fetchApi<{
        userRole: UserRoleAssignment;
      }>({
        url: `/v2/roles/users/${userId}/${payload.roleId}`,
        method: "PATCH",
        data: { scope: payload.scope },
      });
      return data.userRole;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-roles", userId] });
      toast({ title: "Role scope updated" });
    },
    onError: () => {
      toast({ title: "Failed to update scope", variant: "destructive" });
    },
  });
};

export const useRemoveUserRole = (userId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (roleId: string) => {
      await fetchApi({
        url: `/v2/roles/users/${userId}/${roleId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-roles", userId] });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast({ title: "Role removed from user" });
    },
    onError: () => {
      toast({
        title: "Failed to remove role",
        variant: "destructive",
      });
    },
  });
};
